import React, { useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_seemantham_001',
  name: 'Seemantham Baby Shower',
  occasionType: 'BABY_SHOWER',
  description: 'A traditional Indian baby shower (Seemantham) invitation featuring a floral arch, Lord Ganesha, and a beautiful illustration of the mother-to-be.',
  isPremium: true,
  tags: ['baby-shower', 'seemantham', 'traditional', 'indian', 'floral'],
  colorScheme: { primary: '#6d28d9', accent: '#fbbf24', bg: '#f0fdf4' },
};

export function SeemanthamBabyShower({ data }: { data: InvitationData }) {
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  
  const babyShowerData = data.customData?.babyShower;
  
  const fatherInLawName = babyShowerData?.fatherInLawName || 'Sri Shyam Reddy';
  const motherInLawName = babyShowerData?.motherInLawName || 'Smt. Amitha';
  const fatherName = babyShowerData?.fatherName || 'Sri Amit Reddy';
  const motherName = babyShowerData?.motherName || 'Smt. Lalitha';
  const parentsName = babyShowerData?.parentsName || data.hostName || 'Lalitha & Amit';

  const rawDate = data.eventDate || new Date().toISOString();
  let formattedDate = 'MAY 15, 2026';
  let dayOfWeek = 'SATURDAY';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase();
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  } catch (e) {}

  const eventTime = data.eventTime || '10:00 AM';
  const venueName = data.eventVenue?.split(',')[0] || 'THE GRAND PALACE';
  const venueAddress = data.eventVenue || '123 CELEBRATION ROAD, HYDERABAD';
  const rsvpName = babyShowerData?.rsvpName || 'Rahul';
  const rsvpPhone = babyShowerData?.rsvpPhone || '+91 98765 43210';
  const message = data.customMessage || 'We request the honor of your presence and blessings on this auspicious occasion.';
  const dressCode = babyShowerData?.dressCode || 'Traditional attire';

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4 flex flex-col items-center justify-center font-sans select-none">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@300;400;500;600;700&family=Pinyon+Script&display=swap');
        .font-serif-seem { font-family: 'Playfair Display', Georgia, serif; }
        .font-sans-seem { font-family: 'Montserrat', sans-serif; }
        .font-cursive-seem { font-family: 'Pinyon Script', cursive; }
        .font-title-seem { font-family: 'Cinzel', serif; }

        .seem-bg {
          background-color: #f0fdf4;
          background-image: url(/assets/seemantham-baby-shower/bg.jpg);
          background-size: cover;
          background-position: top center;
          background-repeat: no-repeat;
          position: relative;
        }

        .gold-foil-text {
          background: linear-gradient(135deg, #78350f 0%, #b45309 30%, #d97706 50%, #b45309 70%, #78350f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0px 1px 1px rgba(255,255,255,0.9));
        }
      `}</style>

      {/* Main Card Frame */}
      <div className="relative w-full max-w-[540px] aspect-[1080/1920] shadow-2xl rounded-2xl overflow-hidden seem-bg flex flex-col p-10 border-[5px] border-white/90">
        
        {/* Lord Ganesha Top */}
        <div className="absolute top-12 inset-x-0 h-16 pointer-events-none z-10 flex justify-center">
          <img 
            src="/assets/seemantham-baby-shower/ganesha.jpg" 
            alt="Lord Ganesha" 
            className="h-full object-contain mix-blend-multiply opacity-90"
            draggable={false}
          />
        </div>

        {/* Lady Bottom Right */}
        <div className="absolute bottom-[-10px] right-[-15px] w-64 pointer-events-none z-10">
          <img 
            src="/assets/seemantham-baby-shower/lady.jpg" 
            alt="Pregnant Lady" 
            className="w-full h-auto object-contain mix-blend-multiply"
            draggable={false}
          />
        </div>

        {/* Typography — centered luxury stationery layout */}
        <div className="relative z-20 flex flex-col justify-between h-full pt-32 pb-10 px-2">
          
          {/* Header section */}
          <div className="text-center">
            <p className="font-title-seem text-[7px] tracking-[0.48em] text-indigo-900/60 uppercase mb-4">
              || SHREE GANESHAY NAMAHA ||
            </p>

            <p className="font-sans-seem text-[9.5px] text-indigo-950 font-medium leading-relaxed mb-6">
              <span className="font-semibold">{motherName} &amp; {fatherName}</span>
              <br /><span className="text-[7.5px] text-indigo-900/60 uppercase my-1 block">AND</span>
              <span className="font-semibold">{motherInLawName} &amp; {fatherInLawName}</span>
            </p>

            <p className="font-title-seem text-[8px] tracking-[0.42em] text-indigo-900/70 uppercase mb-6">
              REQUEST THE HONOR OF YOUR PRESENCE AT THE
            </p>

            <h2 className="font-title-seem text-[3.8rem] text-[#5d1715] tracking-[0.08em] font-semibold leading-[1] uppercase mb-8 gold-foil-text">
              SEEMANTHAM
            </h2>

            <span className="font-title-seem text-[8px] text-indigo-900/70 tracking-[0.42em] block mb-6 uppercase">
              OF PARENTS TO BE
            </span>

            <h1 className="font-cursive-seem text-[4.8rem] text-[#5d1715] leading-none mb-8 gold-foil-text">
              {parentsName}
            </h1>

            <svg viewBox="0 0 220 12" className="w-36 h-3 mx-auto mb-5" fill="none">
              <line x1="0" y1="6" x2="88" y2="6" stroke="#5d1715" strokeWidth="0.8" opacity="0.40"/>
              <circle cx="110" cy="6" r="2.2" fill="#5d1715" opacity="0.48"/>
              <line x1="132" y1="6" x2="220" y2="6" stroke="#5d1715" strokeWidth="0.8" opacity="0.40"/>
            </svg>
          </div>

          {/* Details Section */}
          <div className="flex flex-col items-center gap-5 text-center font-sans-seem flex-grow justify-center pb-20">
            
            <div className="w-full max-w-[360px] mx-auto">
              <div className="border-t border-b border-[#5d1715]/30 py-4 flex items-center justify-center">
                <div className="flex-1 flex flex-col items-center">
                  <span className="font-title-seem text-[7px] tracking-[0.32em] text-[#5d1715]/60 uppercase block mb-1">DAY</span>
                  <span className="font-title-seem text-[9px] tracking-wider text-[#5d1715] font-semibold block">{dayOfWeek}</span>
                  <span className="font-serif-seem text-[11.5px] text-indigo-950 font-semibold block mt-0.5">{formattedDate}</span>
                </div>
                <div className="w-px h-10 bg-[#5d1715]/20 mx-5 shrink-0" />
                <div className="flex-1 flex flex-col items-center">
                  <span className="font-title-seem text-[7px] tracking-[0.32em] text-[#5d1715]/60 uppercase block mb-1">TIME</span>
                  <span className="font-title-seem text-[9px] tracking-wider text-[#5d1715] font-semibold block">AT</span>
                  <span className="font-serif-seem text-[11.5px] text-indigo-950 font-semibold block mt-0.5">{eventTime}</span>
                </div>
              </div>
            </div>

            <div className="w-full max-w-[320px] mx-auto text-center">
              <span className="font-title-seem text-[7px] tracking-[0.35em] text-[#5d1715]/60 uppercase block mb-1.5">VENUE</span>
              <span className="font-title-seem text-[10px] tracking-[0.18em] text-[#5d1715] font-semibold block uppercase">{venueName}</span>
              <span className="font-sans-seem text-[9px] text-indigo-900 block leading-relaxed mt-1">{venueAddress}</span>
            </div>

            <div
              role="button"
              aria-label="RSVP Now"
              onClick={() => setShowRsvpModal(true)}
              className="cursor-pointer group z-20"
            >
              <div
                className="px-9 py-2.5 rounded-full border border-[#5d1715]/30 bg-gradient-to-r from-[#fef3c7] via-white to-[#fef3c7] group-hover:from-[#fde68a] group-hover:to-[#fde68a] transition-all duration-200"
                style={{ boxShadow: '0 2px 14px rgba(93,23,21,0.06)' }}
              >
                <span className="font-title-seem text-[8.5px] tracking-[0.38em] text-[#5d1715] font-semibold uppercase block text-center">RSVP</span>
                <span className="font-sans-seem text-[9.5px] text-stone-700 font-medium block text-center mt-0.5">{rsvpName} · {rsvpPhone}</span>
              </div>
            </div>

          </div>

          {/* Bottom Section */}
          <div className="text-center pt-2">
            <span className="font-serif-seem text-[10px] text-indigo-900 block leading-relaxed max-w-[280px] mx-auto mb-3 pr-20">
              "{message}"
            </span>

            <span className="font-serif-seem text-[11px] text-[#5d1715]/80 block tracking-wide pr-20">
              Hosted by
            </span>
            <span className="font-title-seem text-[9.5px] tracking-[0.28em] text-indigo-950 font-semibold block mt-1 uppercase pr-20">
              {data.hostName || parentsName}
            </span>
          </div>

        </div>

      </div>

      {/* RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-stone-950/65 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          
          <div className="relative w-full max-w-md bg-stone-900 border border-white/10 rounded-3xl p-6 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-serif text-white font-semibold">Are You Attending?</h3>
              <p className="text-white/60 text-sm mt-1">Please RSVP to let us know if you're coming!</p>
            </div>
            
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: SeemanthamBabyShower });
